/**
 * CDP SDK Event Queue
 * Queue management for offline/failed events with localStorage persistence
 */

import type { EventPayload, QueuedEvent } from './types';
import { STORAGE_KEYS } from './types';
import { log, warn, getNow } from './utils';
import { getStorage, setStorage } from './storage';

// =============================================================================
// CONSTANTS
// =============================================================================

// Maximum number of events to keep in queue
const MAX_QUEUE_SIZE = 100;

// Maximum age of events in queue (24 hours)
const MAX_EVENT_AGE = 24 * 60 * 60 * 1000;

// Maximum retry attempts before dropping event
const MAX_RETRY_ATTEMPTS = 9; // 3 retries × 3 total attempts

// =============================================================================
// IN-MEMORY QUEUE
// =============================================================================

// In-memory queue for events before init
let memoryQueue: QueuedEvent[] = [];

// Flag to prevent concurrent flushes
let isFlushing = false;

// =============================================================================
// QUEUE OPERATIONS
// =============================================================================

/**
 * Add an event to the queue
 */
export function enqueue(payload: EventPayload): void {
  const queuedEvent: QueuedEvent = {
    payload,
    attempts: 0,
    lastAttempt: 0,
    createdAt: getNow(),
  };

  memoryQueue.push(queuedEvent);
  log('Event queued:', payload.type, memoryQueue.length, 'in queue');

  // If queue is getting large, persist to storage
  if (memoryQueue.length >= 10) {
    persistQueue();
  }
}

/**
 * Get all queued events
 */
export function getQueue(): QueuedEvent[] {
  return [...memoryQueue];
}

/**
 * Clear the in-memory queue
 */
export function clearMemoryQueue(): void {
  memoryQueue = [];
  log('Memory queue cleared');
}

/**
 * Get queue size
 */
export function getQueueSize(): number {
  return memoryQueue.length;
}

// =============================================================================
// PERSISTENCE
// =============================================================================

/**
 * Load persisted queue from localStorage
 */
export function loadPersistedQueue(): QueuedEvent[] {
  const stored = getStorage<QueuedEvent[]>(STORAGE_KEYS.EVENT_QUEUE);
  return stored || [];
}

/**
 * Persist current queue to localStorage
 */
export function persistQueue(): void {
  if (memoryQueue.length === 0) {
    return;
  }

  // Get existing persisted queue
  const persisted = loadPersistedQueue();

  // Merge with memory queue (avoid duplicates based on messageId)
  const messageIds = new Set(persisted.map((e) => e.payload.messageId));
  const newEvents = memoryQueue.filter((e) => !messageIds.has(e.payload.messageId));

  const merged = [...persisted, ...newEvents];

  // Trim to max size (keep newest)
  const trimmed = merged.slice(-MAX_QUEUE_SIZE);

  setStorage(STORAGE_KEYS.EVENT_QUEUE, trimmed);
  log('Queue persisted:', trimmed.length, 'events');
}

/**
 * Clear persisted queue
 */
export function clearPersistedQueue(): void {
  setStorage(STORAGE_KEYS.EVENT_QUEUE, []);
  log('Persisted queue cleared');
}

/**
 * Merge persisted queue with memory queue
 */
export function mergePersistedQueue(): void {
  const persisted = loadPersistedQueue();

  if (persisted.length === 0) {
    return;
  }

  // Filter out old events and events with too many attempts
  const now = getNow();
  const valid = persisted.filter((event) => {
    const age = now - event.createdAt;
    if (age > MAX_EVENT_AGE) {
      log('Dropping old event:', event.payload.type);
      return false;
    }
    if (event.attempts >= MAX_RETRY_ATTEMPTS) {
      log('Dropping event after max retries:', event.payload.type);
      return false;
    }
    return true;
  });

  // Merge with memory queue
  const messageIds = new Set(memoryQueue.map((e) => e.payload.messageId));
  const newFromPersisted = valid.filter((e) => !messageIds.has(e.payload.messageId));

  memoryQueue = [...newFromPersisted, ...memoryQueue];

  // Clear persisted queue (will be re-persisted after flush)
  clearPersistedQueue();

  log('Merged', newFromPersisted.length, 'events from persisted queue');
}

// =============================================================================
// FLUSH OPERATIONS
// =============================================================================

/**
 * Set flushing flag
 */
export function setFlushing(value: boolean): void {
  isFlushing = value;
}

/**
 * Check if queue is currently flushing
 */
export function isFlushing_(): boolean {
  return isFlushing;
}

/**
 * Get next event to send
 */
export function dequeue(): QueuedEvent | undefined {
  return memoryQueue.shift();
}

/**
 * Re-queue a failed event for retry
 */
export function requeueForRetry(event: QueuedEvent): void {
  event.attempts += 1;
  event.lastAttempt = getNow();

  if (event.attempts >= MAX_RETRY_ATTEMPTS) {
    log('Event dropped after max retries:', event.payload.type);
    return;
  }

  // Add back to queue (at the end)
  memoryQueue.push(event);
  log('Event requeued for retry:', event.payload.type, 'attempt:', event.attempts);
}

/**
 * Remove a specific event from queue by messageId
 */
export function removeFromQueue(messageId: string): void {
  const index = memoryQueue.findIndex((e) => e.payload.messageId === messageId);
  if (index !== -1) {
    memoryQueue.splice(index, 1);
  }
}

// =============================================================================
// QUEUE UTILITIES
// =============================================================================

/**
 * Get oldest event timestamp
 */
export function getOldestEventTime(): number | null {
  if (memoryQueue.length === 0) {
    return null;
  }
  return Math.min(...memoryQueue.map((e) => e.createdAt));
}

/**
 * Get total retry attempts across all events
 */
export function getTotalRetryAttempts(): number {
  return memoryQueue.reduce((sum, e) => sum + e.attempts, 0);
}

/**
 * Check if queue has pending events
 */
export function hasPendingEvents(): boolean {
  return memoryQueue.length > 0;
}

/**
 * Cleanup old events from queue
 */
export function cleanupOldEvents(): number {
  const now = getNow();
  const before = memoryQueue.length;

  memoryQueue = memoryQueue.filter((event) => {
    const age = now - event.createdAt;
    return age <= MAX_EVENT_AGE;
  });

  const removed = before - memoryQueue.length;
  if (removed > 0) {
    log('Cleaned up', removed, 'old events from queue');
  }

  return removed;
}
